import React, { useState } from "react";

const ShareholderPercentageAlert = ({ percentageChanges, isVisible }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  if (
    !isVisible ||
    !percentageChanges ||
    Object.keys(percentageChanges).length === 0
  ) {
    return null;
  }

  const changes = Object.values(percentageChanges);
  const hasChanges = changes.length > 0;

  if (!hasChanges) {
    return null;
  }

  const totalChanges = changes.length;
  const increasedCount = changes.filter((change) => change.hasIncreased).length;
  const decreasedCount = changes.filter((change) => change.hasDecreased).length;

  return (
    <div className="mt-4 bg-orange-50 border border-orange-200 rounded-md">
      <div
        className="flex items-center justify-between p-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center space-x-3">
          <div className="flex-shrink-0">
            <svg
              className="w-5 h-5 text-orange-600"
              fill="currentColor"
              viewBox="0 0 20 20"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
          </div>
          <div className="flex-1">
            <h3 className="text-sm font-medium text-orange-800">
              Shareholding & Voting Changes Detected
            </h3>
            <p className="text-sm text-orange-700">
              {totalChanges} shareholder{totalChanges !== 1 ? "s" : ""} affected
              {increasedCount > 0 && (
                <span className="ml-1">
                  ({increasedCount} increased
                  {decreasedCount > 0 && `, ${decreasedCount} decreased`})
                </span>
              )}
              {increasedCount === 0 && decreasedCount > 0 && (
                <span className="ml-1">({decreasedCount} decreased)</span>
              )}
            </p>
          </div>
        </div>
        <div className="flex-shrink-0">
          <svg
            className={`w-4 h-4 text-orange-600 transform transition-transform duration-200 ${
              isExpanded ? "rotate-180" : ""
            }`}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-orange-200 px-4 pb-4">
          <div className="mt-3 space-y-2">
            <h4 className="text-sm font-medium text-orange-800 mb-2">
              Detailed Changes:
            </h4>
            <div className="space-y-3">
              {changes.map((change, index) => (
                <div
                  key={index}
                  className="text-sm border-b border-orange-200 pb-2 last:border-0 last:pb-0"
                >
                  <div className="flex items-center space-x-2 mb-1">
                    <span className="font-medium text-gray-900">
                      {change.name}
                    </span>
                    {(change.hasIncreased || change.votingRightsChange > 0) && (
                      <svg
                        className="w-4 h-4 text-green-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M3.293 9.707a1 1 0 010-1.414l6-6a1 1 0 011.414 0l6 6a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L4.707 9.707a1 1 0 01-1.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                    {(change.hasDecreased || change.votingRightsChange < 0) && (
                      <svg
                        className="w-4 h-4 text-red-600"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 10.293a1 1 0 010 1.414l-6 6a1 1 0 01-1.414 0l-6-6a1 1 0 111.414-1.414L9 14.586V3a1 1 0 012 0v11.586l4.293-4.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>

                  {/* Shareholding Row */}
                  <div className="flex items-center justify-between text-xs pl-2 mb-1">
                    <span className="text-gray-600 w-24">Shareholding:</span>
                    <div className="flex items-center space-x-2 flex-1 justify-end">
                      <span className="text-gray-600">
                        {change.originalPercentage.toFixed(2)}%
                      </span>
                      <span className="text-gray-400">→</span>
                      <span
                        className={`font-medium ${
                          change.hasIncreased
                            ? "text-green-600"
                            : change.hasDecreased
                            ? "text-red-600"
                            : "text-gray-900"
                        }`}
                      >
                        {change.newPercentage.toFixed(2)}%
                      </span>
                      <span
                        className={`text-xs font-medium w-16 text-right ${
                          change.hasIncreased
                            ? "text-green-600"
                            : change.hasDecreased
                            ? "text-red-600"
                            : "text-gray-600"
                        }`}
                      >
                        ({change.change > 0 ? "+" : ""}
                        {change.change.toFixed(2)}%)
                      </span>
                    </div>
                  </div>

                  {/* Voting Rights Row */}
                  <div className="flex items-center justify-between text-xs pl-2">
                    <span className="text-gray-600 w-24">Voting Rights:</span>
                    <div className="flex items-center space-x-2 flex-1 justify-end">
                      <span className="text-gray-600">
                        {(change.originalVotingPercentage || 0).toFixed(2)}%
                      </span>
                      <span className="text-gray-400">→</span>
                      <span
                        className={`font-medium ${
                          change.votingRightsChange > 0
                            ? "text-green-600"
                            : change.votingRightsChange < 0
                            ? "text-red-600"
                            : "text-gray-900"
                        }`}
                      >
                        {(change.newVotingPercentage || 0).toFixed(2)}%
                      </span>
                      <span
                        className={`text-xs font-medium w-16 text-right ${
                          change.votingRightsChange > 0
                            ? "text-green-600"
                            : change.votingRightsChange < 0
                            ? "text-red-600"
                            : "text-gray-600"
                        }`}
                      >
                        ({change.votingRightsChange > 0 ? "+" : ""}
                        {(change.votingRightsChange || 0).toFixed(2)}%)
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShareholderPercentageAlert;
